import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Rendu markdown pour les réponses OnX (copie de PilotMarkdown, découplée).
 * Support : **gras**, *italique*, listes, tableaux GFM, `code inline`,
 * blocs de code, liens, titres h1-h4.
 *
 * Pas de rehype-raw : on ne veut pas de HTML arbitraire.
 *
 * react-markdown 10 : plus de prop `className` (une erreur si on la passe),
 * d'ou le <div> autour ; plus de prop `inline` sur `code`, d'ou le style de
 * bloc porte par `pre`, qui neutralise celui du `code` qu'il contient.
 */
const OnXMarkdown = ({ content }) => {
  if (!content) return null;

  return (
    <div className="onx-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Titres
          h1: ({ children }) => (
            <h3 className="text-base font-semibold text-gray-900 mt-4 mb-2 first:mt-0">{children}</h3>
          ),
          h2: ({ children }) => (
            <h4 className="text-sm font-semibold text-gray-900 mt-3 mb-1.5 first:mt-0">{children}</h4>
          ),
          h3: ({ children }) => (
            <h5 className="text-sm font-medium text-gray-800 mt-3 mb-1 first:mt-0">{children}</h5>
          ),
          h4: ({ children }) => (
            <h6 className="text-sm font-medium text-gray-700 mt-2 mb-1 first:mt-0">{children}</h6>
          ),

          // Paragraphe
          p: ({ children }) => (
            <p className="text-sm leading-relaxed text-gray-700 mb-2 last:mb-0">{children}</p>
          ),

          // Gras / italique
          strong: ({ children }) => (
            <strong className="font-semibold text-gray-900">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic text-gray-600">{children}</em>
          ),

          // Listes
          ul: ({ children }) => (
            <ul className="list-disc list-outside ml-4 mb-2 space-y-0.5 text-sm text-gray-700">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-outside ml-4 mb-2 space-y-0.5 text-sm text-gray-700">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="leading-relaxed pl-1">{children}</li>
          ),

          // Code inline (dans un bloc, `pre` ci-dessous en reprend le style)
          code: ({ className: codeClassName, children }) => (
            <code className={`px-1.5 py-0.5 bg-gray-100 text-gray-800 rounded text-xs font-mono ${codeClassName || ''}`}>
              {children}
            </code>
          ),

          // Bloc de code
          pre: ({ children }) => (
            <div className="my-2 rounded-lg overflow-hidden bg-gray-900">
              <pre className="p-3 overflow-x-auto text-xs font-mono text-gray-100 [&_code]:p-0 [&_code]:bg-transparent [&_code]:text-gray-100">
                {children}
              </pre>
            </div>
          ),

          // Tableaux — style financier lisible
          table: ({ children }) => (
            <div className="my-3 overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full text-xs">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-gray-50 border-b border-gray-200">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="divide-y divide-gray-100">{children}</tbody>
          ),
          tr: ({ children }) => (
            <tr className="hover:bg-gray-50/50 transition-colors">{children}</tr>
          ),
          th: ({ children }) => (
            <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
              {children}
            </th>
          ),
          td: ({ children }) => {
            // Aligner à droite les cellules qui ressemblent à des montants
            const text = String(children?.[0] || children || '');
            const isAmount = /^\s*-?\s*[\d\s,.]+\s*\$?\s*$/.test(text) || /^\$\s*[\d,.]+/.test(text);
            return (
              <td className={`px-3 py-2 text-gray-700 whitespace-nowrap ${isAmount ? 'text-right font-mono tabular-nums' : ''}`}>
                {children}
              </td>
            );
          },

          // Liens
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary-dark underline underline-offset-2 transition-colors"
            >
              {children}
            </a>
          ),

          // Separateur
          hr: () => <hr className="my-3 border-gray-200" />,

          // Blockquote
          blockquote: ({ children }) => (
            <blockquote className="my-2 pl-3 border-l-2 border-gray-300 text-gray-600 italic text-sm">
              {children}
            </blockquote>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default OnXMarkdown;
